import _ from 'lodash'
import { PropsWithChildren, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CellContext, createColumnHelper, RowData } from '@tanstack/react-table'

import { Chip, ChipProps, Paper, Stack, Tooltip } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import LaunchIcon from '@mui/icons-material/Launch'
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation'

import ChipButton from '@/components/ui/buttons/ChipButton'
import Table from '@/components/ui/tables/Table'

import { OrderStatus } from '@/core/models/order.model'
import { Link } from 'react-router-dom'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    acceptOrder?: (orderId: string) => void
  }
}

type ExpertiseOrderData = {
  id: string
  acceptedBy?: string
  canAccept?: boolean
  canOpen?: boolean
  createdBy: string
  consultationId?: string
  expertiseId: string
  isAccepting?: boolean
  patient: string
  speciality: string
  status: string
}

type ExpertiseOrdersProps = {
  expertiseOrders: ExpertiseOrderData[]
  onAccept?: (consultationOrderId: string) => void
  readOnly?: boolean
}

const columnHelper = createColumnHelper<ExpertiseOrderData>()

export default function ExpertiseOrders(props: ExpertiseOrdersProps) {
  const { expertiseOrders, onAccept, readOnly } = props
  const { t } = useTranslation()

  const columns = useMemo(
    () =>
      _.compact([
        columnHelper.accessor('createdBy', {
          header: t('table.expertiseOrders.header.createdBy').toString(),
          cell: CellDecorator,
        }),
        columnHelper.accessor('acceptedBy', {
          header: t('table.expertiseOrders.header.acceptedBy').toString(),
        }),
        columnHelper.accessor('patient', {
          header: t('table.expertiseOrders.header.patient').toString(),
          cell: CellDecorator,
        }),
        columnHelper.accessor('speciality', {
          header: t('table.expertiseOrders.header.speciality').toString(),
        }),
        columnHelper.accessor('status', {
          header: t('table.expertiseOrders.header.status').toString(),
          cell: ConsultationOrderStatusCell,
        }),
        !readOnly &&
          columnHelper.display({
            id: 'actions',
            header: t('common:table.header.action'),
            cell: ActionsCell,
            meta: {
              align: 'right',
            },
          }),
      ]),
    [readOnly, t]
  )

  return (
    <Table
      key="network-expertise-orders"
      container={TableContainer}
      columns={columns}
      data={expertiseOrders}
      infiniteScroll
      manualPagination
      meta={{
        acceptOrder: onAccept,
      }}
    />
  )
}

function TableContainer({ children }: PropsWithChildren<{}>) {
  const isMobile = useIsMobile()
  return <Paper sx={{ minHeight: isMobile ? '200px' : '600px' }}>{children}</Paper>
}

function ActionsCell(props: CellContext<ExpertiseOrderData, unknown>) {
  const { row, table } = props
  const { t } = useTranslation()

  const { canAccept, canOpen, consultationId, expertiseId, id, isAccepting, status } = row.original
  return (
    <Stack direction="row" alignItems="center" justifyContent="end" spacing={1}>
      {canAccept && status === OrderStatus.Pending && (
        <Tooltip title={t('common:button.accept.tooltip')} placement="top">
          <ChipButton
            loading={isAccepting}
            color="success"
            onClick={() => {
              table.options.meta?.acceptOrder?.(id)
            }}
          >
            <CheckRoundedIcon />
          </ChipButton>
        </Tooltip>
      )}
      {consultationId && (
        <Tooltip title={t('table.expertiseOrders.actions.openConsultation.tooltip')} placement="top">
          <ChipButton color="info" component={Link} to={`/consultations/${consultationId}`}>
            <MedicalInformationIcon />
          </ChipButton>
        </Tooltip>
      )}
      {canOpen && (
        <Tooltip title={t('table.expertiseOrders.actions.openExpertise.tooltip')} placement="top">
          <ChipButton color="info" component={Link} to={`/expertises/${expertiseId}`}>
            <LaunchIcon />
          </ChipButton>
        </Tooltip>
      )}
    </Stack>
  )
}

function CellDecorator({ getValue }: CellContext<ExpertiseOrderData, string>) {
  return (
    <Stack alignItems="start" justifyContent="center" sx={{ minHeight: '30px' }}>
      {getValue()}
    </Stack>
  )
}

function ConsultationOrderStatusCell({ getValue }: CellContext<ExpertiseOrderData, string>) {
  let color: ChipProps['color']
  const status = getValue()
  switch (status) {
    case OrderStatus.Consultation_Created:
    case OrderStatus.Pending:
      color = 'primary'
      break
    case OrderStatus.Accepted:
      color = 'success'
      break
    case OrderStatus.Canceled:
    case OrderStatus.Refused:
      color = 'error'
      break
    default:
      color = 'info'
      break
  }
  return <Chip color={color} label={status} size="small" variant="filled" />
}
